import type { BattleHex } from "@/view/renderers/BattleHex";
import type { UnitStack } from "@/view/renderers/UnitStack";

export class EntityRegistry {
    private hexes = new Map<string, BattleHex>();
    private units = new Map<string, UnitStack>();
    
    // ═══════════════════════════════════════════════════════════════════════
    // HEX MANAGEMENT
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Register a hex visual object
     */
    registerHex(id: string, hex: BattleHex): void {
        this.hexes.set(id, hex);
    }
    
    /**
     * Get hex by ID
     */
    getHex(id: string): BattleHex | undefined {
        return this.hexes.get(id);
    }
    
    /**
     * Get all hexes
     */
    getAllHexes(): Map<string, BattleHex> {
        return this.hexes;
    }
    
    /**
     * Check if hex exists
     */
    hasHex(id: string): boolean {
        return this.hexes.has(id);
    }
    
    /**
     * Remove hex
     */
    removeHex(id: string): void {
        const hex = this.hexes.get(id);
        if (hex) {
            hex.destroy();
            this.hexes.delete(id);
        }
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // UNIT MANAGEMENT
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Register a unit visual object
     */
    registerUnit(id: string, unit: UnitStack): void {
        this.units.set(id, unit);
    }
    
    /**
     * Get unit by ID
     */
    getUnit(id: string): UnitStack | undefined {
        return this.units.get(id);
    }
    
    /**
     * Get all units
     */
    getAllUnits(): Map<string, UnitStack> {
        return this.units;
    }
    
    /**
     * Check if unit exists
     */
    hasUnit(id: string): boolean {
        return this.units.has(id);
    }
    
    /**
     * Remove unit
     */
    removeUnit(id: string): void {
        const unit = this.units.get(id);
        if (unit) {
            unit.destroy();
            this.units.delete(id);
        }
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // CLEANUP
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Clear all hexes
     */
    clearHexes(): void {
        this.hexes.forEach(h => h.destroy());
        this.hexes.clear();
    }
    
    /**
     * Clear all units
     */
    clearUnits(): void {
        this.units.forEach(u => u.destroy());
        this.units.clear();
    }
    
    /**
     * Destroy all entities and clear registry
     */
    destroy(): void {
        this.clearHexes();
        this.clearUnits();
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // UTILITY
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Get counts for debugging
     */
    getCounts(): { hexes: number; units: number } {
        return {
            hexes: this.hexes.size,
            units: this.units.size
        };
    }
}